import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import Style from "../style/blog.module.scss";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { fetchBlogs } from "../redux/Slice";
import { useParams, useSearchParams } from "react-router-dom";
import Loader from "../Loader/Loader";

const Blog = () => {
  const { category } = useParams();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const currentPage = Number(searchParams.get("page")) || 1;
  const baseUrl = "https://backend.onedialusa.com/assets/img/blogs/";
  const [totalPages, setTotalPages] = useState(1);
  const [blogs, setBlogs] = useState<any[]>([]);
  const blogsCache = useRef<{ [key: number]: any[] }>({});
  const firstPageData = useAppSelector(
    (state) => state.detail.blogs?.blogs.blogs.data
  );
  useEffect(() => {
    if (firstPageData && firstPageData.length) {
      blogsCache.current[1] = firstPageData;
    }
  }, [firstPageData]);

  const fetchBlogsData = useCallback(async () => {
    if (blogsCache.current[currentPage]) {
      setBlogs(blogsCache.current[currentPage]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await dispatch(
        fetchBlogs({ page: currentPage, category: category || "" })
      ).unwrap();

      const data = response?.blogs?.blogs?.data || [];
      const meta = response?.blogs?.blogs?.last_page || 1;

      blogsCache.current[currentPage] = data;
      setBlogs(data);
      setTotalPages(meta);
    } catch (err) {
      console.error("Error fetching blogs", err);
      setBlogs([]);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, currentPage, category]);

  useEffect(() => {
    fetchBlogsData();
  }, [fetchBlogsData]);

  const extractPTags = useCallback((html: string) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    const pTags = Array.from(tempDiv.querySelectorAll("p")).slice(0, 2);
    return pTags
      .map((p) => p.textContent?.trim())
      .filter(Boolean)
      .join(" ");
  }, []);

  const goToPage = (page: number) => {
    setSearchParams({ page: String(page) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const paginationButtons = useMemo(() => {
    const buttons = [];
    const max = 5;
    const half = Math.floor(max / 2);
    let start = Math.max(currentPage - half, 1);
    let end = Math.min(currentPage + half, totalPages);

    if (end - start < max - 1) {
      if (start > 1) end = Math.min(start + max - 1, totalPages);
      else start = Math.max(end - max + 1, 1);
    }

    if (start > 1) buttons.push(1, "...");
    for (let i = start; i <= end; i++) buttons.push(i);
    if (end < totalPages) buttons.push("...", totalPages);

    return buttons;
  }, [currentPage, totalPages]);

  const getImageSrc = (image: string) => {
    if (!image) {
      return "https://via.placeholder.com/300x200?text=Blog+Image";
    }
    if (image.startsWith("http://") || image.startsWith("https://")) {
      return image;
    }
    return `${baseUrl}${image}`;
  };

  return (
    <div className={Style.container}>
      <div className={Style.blogGround}>
        <div className={Style.wrapper}>
          <div className={Style.breadcrumb}>
            <a href="/">Home</a>
            <span> › </span>
            {category ? (
              <>
                <a href="/blog">Blog</a>
                <span> › </span>
                <span>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </span>
              </>
            ) : (
              <span>Blog</span>
            )}
          </div>

          <div className={Style.title}>
            <h2>{category ? category.toUpperCase() : "All Blogs"}</h2>
          </div>
        </div>
      </div>

      <div className={Style.wrapper}>
        <div className={Style.Article}>
          {isLoading && blogs.length === 0 ? (
            <Loader />
          ) : blogs.length ? (
            blogs.map((item) => (
              <a
                key={item.id}
                href={`/blog/${
                  item.title?.toLowerCase()?.replaceAll(" ", "-") ||
                  "default-title"
                }`}
              >
                <div className={Style.ArticleData}>
                  <div className={Style.ArticleImageContainer}>
                    <img
                      src={getImageSrc(item.image)}
                      alt={item.title || "Blog Image"}
                      width="100%"
                      height="auto"
                      style={{ objectFit: "cover" }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "https://via.placeholder.com/300x200?text=Blog+Image";
                      }}
                    />
                  </div>
                  <div className={Style.ArticleText}>
                    <h2>{item.title || "No Title Available"}</h2>
                    <p className={Style.description}>
                      {extractPTags(item.content)}
                    </p>
                    <span>Read More</span>
                  </div>
                </div>
              </a>
            ))
          ) : (
            <p>No blogs available.</p>
          )}
        </div>

        {!isLoading && totalPages > 1 && (
          <div className={Style.pagination}>
            {currentPage > 1 && (
              <button onClick={() => goToPage(currentPage - 1)}>
                &laquo; Prev
              </button>
            )}
            {paginationButtons.map((btn, index) =>
              btn === "..." ? (
                <span key={index}>...</span>
              ) : (
                <button
                  key={btn}
                  onClick={() => goToPage(Number(btn))}
                  className={btn === currentPage ? Style.activePage : ""}
                >
                  {btn}
                </button>
              )
            )}
            {currentPage < totalPages && (
              <button onClick={() => goToPage(currentPage + 1)}>
                Next &raquo;
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
